import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, ChevronRight, Phone } from 'lucide-react';
import { getPageContent, menuData, practiceInfo } from '../data/siteData';
import { additionalPageContent } from '../data/pageContent';
import './InnerPage.css';

const RELATED_SECTION_TITLES = {
  '/our-practice': 'Our Team',
  '/cosmetic-dentistry': 'Cosmetic Procedures',
  '/procedures/cleanings-prevention': 'Prevention Procedures',
  '/procedures/periodontal-disease': 'Periodontal Procedures',
  '/procedures/restorations': 'Restoration Procedures',
  '/procedures/orthodontics': 'Orthodontic Procedures',
  '/procedures/oral-surgery': 'Surgical Procedures',
  '/procedures/periodontics': 'Periodontic Procedures',
  '/procedures': 'Related Procedures',
  '/education-and-resources': 'Knowledge Center',
};

const DEFAULT_RELATED_TITLE = 'Related Services';

function getRelatedTitle(path) {
  // Try most specific match first (longer prefixes)
  const sortedKeys = Object.keys(RELATED_SECTION_TITLES)
    .sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    if (path === key || path.startsWith(key + '/')) {
      return RELATED_SECTION_TITLES[key];
    }
  }
  return DEFAULT_RELATED_TITLE;
}

function InnerPage() {
  const location = useLocation();
  const path = location.pathname;
  
  // Get content from both sources
  let content = getPageContent(path);
  if (additionalPageContent[path]) {
    content = additionalPageContent[path];
  }

  // Generate breadcrumbs from path
  const pathParts = path.split('/').filter(Boolean);
  const breadcrumbs = pathParts.map((part, index) => {
    const url = '/' + pathParts.slice(0, index + 1).join('/');
    const title = part
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace(/\?/g, '');
    return { title, url };
  });

  // Find related pages (siblings in menu structure)
  const findRelatedPages = () => {
    // Search through menu for current page's siblings
    const searchMenu = (items) => {
      for (const item of items) {
        if (item.path === path && item.submenu) {
          // Current page has children - show them
          return item.submenu.slice(0, 6);
        }
        if (item.submenu) {
          // Check if current page is in this submenu
          const inSubmenu = item.submenu.find(sub => sub.path === path);
          if (inSubmenu) {
            // Return siblings
            return item.submenu.filter(sub => sub.path !== path).slice(0, 6);
          }
          // Check nested submenus
          for (const sub of item.submenu) {
            if (sub.submenu) {
              const inNestedSubmenu = sub.submenu.find(nested => nested.path === path);
              if (inNestedSubmenu) {
                return sub.submenu.filter(nested => nested.path !== path).slice(0, 6);
              }
            }
          }
        }
      }
      return [];
    };
    
    return searchMenu(menuData.mainMenu);
  };

  const relatedPages = findRelatedPages();

  /**
   * Format content - supports both HTML and plain text
   * 
   * HTML Content Support:
   * - <p> paragraphs
   * - <strong> and <b> for bold text
   * - <em> and <i> for italic text
   * - <h3> and <h4> for headings
   * - <ul> and <ol> for lists
   * - <li> for list items
   * - <a> for links
   * - <br> for line breaks
   * - <span> with inline styles
   * 
   * Example usage in pageContent.js:
   * content: `
   *   <p>At P3 Dental, we believe <strong>everyone deserves a beautiful smile</strong>.</p>
   *   <h3>Our Services</h3>
   *   <ul>
   *     <li><strong>Teeth Whitening</strong> – Brighten your smile</li>
   *     <li><strong>Dental Implants</strong> – Permanent solutions</li>
   *   </ul>
   * `
   */
  const formatContent = (text) => {
    if (!text) return null;
    
    // Check if content contains HTML tags
    const hasHTML = /<[a-z][\s\S]*>/i.test(text);
    
    if (hasHTML) {
      // Render HTML content directly using dangerouslySetInnerHTML
      // This supports: <p>, <strong>, <b>, <em>, <i>, <h3>, <h4>, <ul>, <ol>, <li>, <a>, <br>, <span>
      return (
        <div 
          className="html-content"
          dangerouslySetInnerHTML={{ __html: text }} 
        />
      );
    }
    
    // Plain text formatting (legacy support for content without HTML tags)
    return text.split('\n\n').map((paragraph, index) => {
      // Check if it's a list item line (contains bullet points)
      if (paragraph.includes('•')) {
        const lines = paragraph.split('\n');
        const title = lines[0].includes('•') ? null : lines[0];
        const items = lines.filter(line => line.includes('•'));
        
        return (
          <div key={index} className="content-list-section">
            {title && <h3>{title}</h3>}
            <ul className="content-list">
              {items.map((item, i) => (
                <li key={i}>{item.replace('•', '').trim()}</li>
              ))}
            </ul>
          </div>
        );
      }
      
      // Check if it's a heading (ends with : and is short)
      if (paragraph.endsWith(':') && paragraph.length < 100) {
        return <h3 key={index}>{paragraph.slice(0, -1)}</h3>;
      }
      
      // Regular paragraph
      return <p key={index}>{paragraph}</p>;
    });
  };

  return (
    <main className="inner-page">
      {/* Page Hero */}
      <div className="page-hero" style={content.image ? { backgroundImage: `url(${content.image})` } : {}}>
        <div className="page-hero-overlay"></div>
        <div className="container">
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.url}>
                <ChevronRight size={14} />
                {index === breadcrumbs.length - 1 ? (
                  <span>{crumb.title}</span>
                ) : (
                  <Link to={crumb.url}>{crumb.title}</Link>
                )}
              </React.Fragment>
            ))}
          </nav>
          <h1>{content.title}</h1>
          {content.subtitle && <p className="page-subtitle">{content.subtitle}</p>}
        </div>
      </div>

      {/* Page Content */}
      <div className="page-content">
        <div className="container">
          <div className="content-grid">
            {/* Main Content */}
            <article className="main-content">
              <div className="content-body">
                {formatContent(content.content)}
              </div>

              {/* Appointment CTA */}
              <div className="content-cta">
                <h3>Ready to Schedule Your Visit?</h3>
                <p>Contact us today to schedule an appointment or learn more about our services.</p>
                <div className="cta-buttons">
                  <Link to="/appointment-request" className="btn btn-primary">
                    <Calendar size={18} />
                    Request Appointment
                  </Link>
                  <a href={`tel:${practiceInfo.phone.replace(/[^0-9]/g, '')}`} className="btn btn-outline-dark">
                    <Phone size={18} />
                    Call {practiceInfo.phone}
                  </a>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="page-sidebar">
              {/* Quick Contact */}
              <div className="sidebar-card contact-card">
                <h3>Contact Us</h3>
                <p><strong>Phone:</strong> {practiceInfo.phone}</p>
                <p><strong>Email:</strong> {practiceInfo.email}</p>
                <p><strong>Address:</strong><br />
                  {practiceInfo.address.street}<br />
                  {practiceInfo.address.city}, {practiceInfo.address.state} {practiceInfo.address.zip}
                </p>
                <Link to="/contact-us" className="btn btn-primary btn-block">
                  Contact Us
                </Link>
              </div>

              {/* Related Pages */}
              {relatedPages.length > 0 && (
                <div className="sidebar-card related-card">
                  <h3>{getRelatedTitle(path)}</h3>
                  <ul className="related-list">
                    {relatedPages.map(page => (
                      <li key={page.id}>
                        <Link to={page.path}>
                          <ChevronRight size={14} />
                          {page.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Office Hours */}
              <div className="sidebar-card hours-card">
                <h3>Office Hours</h3>
                <ul className="hours-list">
                  {practiceInfo.hours.map((schedule, index) => (
                    <li key={index}>
                      <span className="day">{schedule.day}</span>
                      <span className="time">
                        {schedule.close ? `${schedule.open} - ${schedule.close}` : schedule.open}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Back Navigation */}
      <div className="page-back">
        <div className="container">
          <button onClick={() => window.history.back()} className="back-link">
            <ArrowLeft size={18} />
            Back to Previous Page
          </button>
        </div>
      </div>
    </main>
  );
}

export default InnerPage;